import { Busylight } from 'components/settings/reducers/sections/personal';

/**
 * Represents the ipc renderer channels for sending and receiving event messages
 * between the web application and the electron app.
 */
export enum IpcRendererChannel {
  AsynchronousMessage = 'asynchronous-message',
  AsynchronousReply = 'asynchronous-reply',
}

/** Represents an electron event with an optional payload. */
export type ElectronEvent<TEventType extends string, TEventPayload = never> = {
  /** The type. */
  type: TEventType;
} & ([TEventPayload] extends [never]
  ? {}
  : {
      /** The payload. */
      readonly payload: TEventPayload;
    });

/** Represents the request events. */
export type ElectronEventRequest =
  | ElectronEvent<'ELECTRON_APP_SIGN_OUT'>
  | ElectronEvent<'ELECTRON_DISPOSE'>
  | ElectronEvent<'ELECTRON_CONVERSATION_OFFER_CANCELLED'>
  | ElectronEvent<
      'ELECTRON_CONVERSATION_OFFER_RECEIVED',
      { busylight: Busylight | null; channel: unknown }
    >
  | ElectronEvent<'ELECTRON_PRESENCE_CHANGE', { status: string }>
  | ElectronEvent<'BUSYLIGHT_RING', { busylight: Busylight | null }>
  | ElectronEvent<'BUSYLIGHT_STOP'>
  | ElectronEvent<'BUSYLIGHT_LIST'>
  | ElectronEvent<'BUSYLIGHT_GET_CONNECTED_STATUS'>
  | ElectronEvent<'BUSYLIGHT_GET'>
  | ElectronEvent<'TWILIO_ESTABLISHED_CALL'>
  | ElectronEvent<'TWILIO_DISCONNECTED_CALL'>
  | ElectronEvent<'JABRA_INIT'>
  | ElectronEvent<'JABRA_LIST'>
  | ElectronEvent<'JABRA_RING'>
  | ElectronEvent<'JABRA_UNRING'>
  | ElectronEvent<'SPELLCHECKER_AUTO_DETECT_LANGUAGE', { text: string }>
  | ElectronEvent<'ELECTRON_CLEAR_EVENT_CACHE'>;

/** Represents the response events. */
export type ElectronEventResponse =
  | ElectronEvent<'ELECTRON_MAKE_CALL', { phoneNumber: string }>
  | ElectronEvent<'ELECTRON_COMPOSE_EMAIL', { mailto: string }>
  | ElectronEvent<'BUSYLIGHT_SELECT_SPECIFIC_SUCCEEDED', { busylight: unknown }>
  | ElectronEvent<'BUSYLIGHT_LIST_SUCCEEDED', { busylights: unknown[] }>
  | ElectronEvent<'BUSYLIGHT_GET_SUCCEEDED', { connected: boolean }>
  | ElectronEvent<'BUSYLIGHT_RING_SUCCEEDED'>
  | ElectronEvent<'BUSYLIGHT_RING_FAILED'>
  | ElectronEvent<'BUSYLIGHT_STOP_SUCCEEDED'>
  | ElectronEvent<
      'BUSYLIGHT_GET_CONNECTED_STATUS_SUCCEEDED',
      { connected: boolean }
    >
  | ElectronEvent<'JABRA_ATTACHED', { connected: true; device: unknown }>
  | ElectronEvent<'JABRA_DETACHED', { connected: false; deviceID: unknown }>
  | ElectronEvent<'JABRA_CLOSED'>
  | ElectronEvent<'JABRA_INIT_SUCCEEDED'>
  | ElectronEvent<'JABRA_INIT_FAILED', { error: Error }>
  | ElectronEvent<'JABRA_LIST_SUCCEEDED', { list: unknown }>
  | ElectronEvent<'JABRA_LIST_FAILED', { error: Error | string }>
  | ElectronEvent<'JABRA_RING_SUCCEEDED', { list: unknown }>
  | ElectronEvent<'JABRA_RING_FAILED', { error: Error }>
  | ElectronEvent<'JABRA_UNRING_SUCCEEDED', { list: unknown }>
  | ElectronEvent<'JABRA_UNRING_FAILED', { error: Error }>
  | ElectronEvent<
      'JABRA_BUTTON_CLICKED',
      { button: unknown; buttonValue: boolean }
    >;
